import { useCallback, useEffect, useState } from 'react';
import { festAPI } from '@/lib/api';
import { toast } from 'sonner';

/* Sommerfest-Dashboard — Live-Zahlen, Leaderboard, Verlosung, Export.
 * Läuft im CRM hinter Login (Backend prüft fest.view / fest.aktionen).
 * Für den Beamer/das Handy am Fest gedacht: dunkel, große Zahlen. */

export default function FestDashboardPage() {
  const [daten, setDaten] = useState(null);
  const [zieheGerade, setZieheGerade] = useState(false);
  const [gewinner, setGewinner] = useState(null);   // frisch gezogen
  const [preis, setPreis] = useState('');

  const laden = useCallback(async () => {
    try { setDaten((await festAPI.uebersicht()).data); }
    catch { toast.error('Übersicht konnte nicht geladen werden.'); }
  }, []);
  useEffect(() => {
    laden();
    const t = setInterval(laden, 30000);
    return () => clearInterval(t);
  }, [laden]);

  const ziehen = async () => {
    setZieheGerade(true);
    setGewinner(null);
    try {
      const r = await festAPI.verlosungZiehen();
      setGewinner(r.data);
      setPreis('');
      laden();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ziehung fehlgeschlagen.');
    }
    setZieheGerade(false);
  };

  const preisSpeichern = async () => {
    if (!gewinner || !preis.trim()) return;
    try {
      await festAPI.verlosungPreis(gewinner.karten_code, preis.trim());
      toast.success('Preis gespeichert.');
      setGewinner(null);
      laden();
    } catch { toast.error('Preis konnte nicht gespeichert werden.'); }
  };

  const exportieren = async () => {
    try {
      const res = await festAPI.exportCSV();
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'festkarten.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export fehlgeschlagen.'); }
  };

  const nachfassen = async () => {
    if (!window.confirm('Nachfass-Aufgaben für alle Karten mit Kontakt-Erlaubnis '
      + 'und ohne Probetraining anlegen (fällig 14.09.)?')) return;
    try {
      const r = await festAPI.nachfass({});
      toast.success(`${r.data.angelegt} Aufgabe(n) angelegt, ${r.data.uebersprungen} gab es schon.`);
    } catch { toast.error('Nachfass-Aufgaben fehlgeschlagen.'); }
  };

  if (!daten) {
    return <div className="fd-root"><style>{CSS}</style>
      <p className="fd-lade">Dashboard wird geladen …</p></div>;
  }

  const KACHELN = [
    ['Karten gesamt', daten.karten_gesamt],
    // Karten sind angemeldete Gaeste; anwesend sind die, die wirklich da sind
    ['Anwesend (eingecheckt)', daten.anwesend ?? 0],
    ['Interesse Kinder', daten.interesse.kinder],
    ['Interesse Frauen', daten.interesse.frauen],
    ['Interesse Erwachsene', daten.interesse.erwachsene],
    ['Nur Verlosung', daten.interesse.nur_verlosung],
    ['Challenges gesamt', daten.challenges_gesamt],
    [`Im Lostopf (≥${daten.verlosung_min_challenges})`, daten.karten_verlosungsreif],
    ['Probetrainings', daten.probetrainings],
    ['Gymfights-Tickets', daten.gymfights_tickets],
    ['Kontakt erlaubt', daten.kontakt_erlaubt],
  ];

  return (
    <div className="fd-root">
      <style>{CSS}</style>
      <header className="fd-kopf">
        <h1>Sommerfest-Dashboard</h1>
        <div className="fd-knoepfe">
          <a className="fd-btn fd-btn-line" href="/fest/scan-voll">Scanner (voll, mit Kurswahl)</a>
          <a className="fd-btn fd-btn-line" href="/fest/scan?wer=Cagla&station=stand">Team-Scan Stand</a>
          <button className="fd-btn fd-btn-line" onClick={exportieren}>CSV-Export</button>
          <button className="fd-btn fd-btn-line" onClick={nachfassen}>Nachfass-Aufgaben</button>
        </div>
      </header>

      <section className="fd-kacheln">
        {KACHELN.map(([label, wert]) => (
          <div key={label} className="fd-kachel">
            <span className="fd-wert">{wert}</span>
            <span className="fd-label">{label}</span>
          </div>
        ))}
      </section>

      <GastSuche />

      <section className="fd-verlosung">
        <h2>Verlosung</h2>
        <button className="fd-btn fd-btn-gross" onClick={ziehen} disabled={zieheGerade}>
          {zieheGerade ? 'Trommelwirbel …' : '🎟️ Los ziehen'}
        </button>

        {gewinner && (
          <div className="fd-gewinner">
            <span className="fd-gewinner-nr">#{gewinner.losnummer}</span>
            <span className="fd-gewinner-name">{gewinner.vorname}</span>
            <span className="fd-gewinner-topf">aus {gewinner.topf_groesse} Losen</span>
            <div className="fd-preisrow">
              <input placeholder="Preis, z. B. 1 Monat gratis" value={preis}
                     onChange={(e) => setPreis(e.target.value)}
                     onKeyDown={(e) => e.key === 'Enter' && preisSpeichern()} />
              <button className="fd-btn" onClick={preisSpeichern}>Speichern</button>
            </div>
          </div>
        )}

        {(daten.verlosungspool || []).length > 0 && (
          <details className="fd-pool" data-testid="fest-verlosungspool">
            <summary>Im Lostopf: {daten.verlosungspool.length} Karte(n) — wer ist teilnahmeberechtigt?</summary>
            <table className="fd-tabelle">
              <thead><tr><th>Los</th><th>Name</th><th>Challenges</th><th>Extra-Los</th><th>Status</th></tr></thead>
              <tbody>
                {daten.verlosungspool.map((p) => (
                  <tr key={p.losnummer}>
                    <td>#{p.losnummer}</td><td>{p.vorname} {p.nachname_kurz}</td><td>{p.challenges}</td>
                    <td>{p.extra_los ? 'Probetraining ✓' : '–'}</td><td>{p.gezogen ? 'gezogen' : 'im Topf'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        )}

        {daten.gewinner.length > 0 && (
          <table className="fd-tabelle">
            <thead><tr><th>Los</th><th>Vorname</th><th>Preis</th></tr></thead>
            <tbody>
              {daten.gewinner.map((g) => (
                <tr key={g.losnummer}>
                  <td>#{g.losnummer}</td><td>{g.vorname}</td>
                  <td>{g.preis || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section>
        <h2>Leaderboard (für die Siegerehrung)</h2>
        <div className="fd-boards">
          {Object.entries(daten.leaderboard).map(([id, b]) => (
            <div key={id} className="fd-board">
              <h3>{b.name} <span className="fd-einheit">({b.einheit})</span></h3>
              {Object.keys(b.kategorien).length === 0 && <p className="fd-leer">Noch keine Werte.</p>}
              {Object.entries(b.kategorien).map(([kat, liste]) => (
                <div key={kat}>
                  <span className="fd-kat">{kat}</span>
                  <ol>
                    {liste.map((e, i) => (
                      <li key={i}>{e.vorname} — <b>{e.wert}</b> {b.einheit}</li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

/* ------------------------------------------------ Gast suchen + zuweisen
   12.09.-Erweiterung Teil 2: Gast ohne Handy / leerer Akku. Eine Hand haelt
   die Pratze — grosse Flaechen, wenige Klicks, Retry bei Funkloch. */
function GastSuche() {
  const [q, setQ] = useState('');
  const [treffer, setTreffer] = useState(null);
  const [fehler, setFehler] = useState('');
  const [aktiv, setAktiv] = useState(null);      // Karte, der zugewiesen wird
  const [kurse, setKurse] = useState(null);
  const [kindName, setKindName] = useState('');
  const [busy, setBusy] = useState(false);
  const [neu, setNeu] = useState(false);
  const [neuForm, setNeuForm] = useState({ vorname: '', nachname: '', telefon: '' });

  const suchen = useCallback(async (wert) => {
    setFehler('');
    if ((wert || '').trim().length < 2) { setTreffer(null); return; }
    try {
      const r = await festAPI.suche(wert.trim());
      setTreffer(r.data.treffer);
    } catch {
      setFehler('Suche fehlgeschlagen — Netz prüfen und nochmal.');
    }
  }, []);
  useEffect(() => {
    const t = setTimeout(() => suchen(q), 300);
    return () => clearTimeout(t);
  }, [q, suchen]);

  const kurseLaden = async (karte) => {
    setAktiv(karte); setKurse(null); setFehler(''); setKindName('');
    try {
      const r = await festAPI.gastKurse(karte.karten_code);
      setKurse(r.data.kurse);
    } catch {
      setFehler('Kurse laden fehlgeschlagen — nochmal tippen.');
    }
  };

  const zuweisen = async (terminId) => {
    if (busy) return;
    setBusy(true); setFehler('');
    try {
      const r = await festAPI.probetraining(aktiv.karten_code, {
        termin_id: terminId, kind_vorname: kindName.trim() });
      toast.success(`Gebucht: ${r.data.anzeige.kurs} am ${r.data.anzeige.datum} ${r.data.anzeige.uhrzeit} Uhr`);
      setAktiv(null); setKurse(null);
      suchen(q);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setFehler(typeof d === 'string' ? d
        : 'NICHT gespeichert (Netz?) — bitte denselben Termin nochmal tippen.');
    } finally {
      setBusy(false);
    }
  };

  const neuAnlegen = async () => {
    if (busy) return;
    const { vorname, nachname, telefon } = neuForm;
    if (!vorname.trim() || !telefon.trim()) {
      setFehler('Mindestens Vorname und Telefonnummer.'); return;
    }
    setBusy(true); setFehler('');
    try {
      // Nutzt die normale Karten-Anlage — der Gast bekommt Karte + Lead wie
      // am Eingang; danach direkt in den Zuweisen-Flow.
      const r = await festAPI.karteAnlegen({
        vorname: vorname.trim(), nachname: nachname.trim() || 'Gast',
        telefon: telefon.trim(),
        // Team-Anlage am Stand: Haftung + Kontakt werden muendlich
        // eingeholt, wie am Eingang — gleiche Felder wie das Formular.
        haftung_akzeptiert: true, datenschutz_akzeptiert: true,
        kontakt_erlaubt: true, interesse: [] });
      toast.success('Gast angelegt');
      setNeu(false); setNeuForm({ vorname: '', nachname: '', telefon: '' });
      setQ(telefon.trim());
      kurseLaden({ karten_code: r.data.karten_code,
                   name: `${vorname} ${nachname}`.trim() });
    } catch (e) {
      const d = e?.response?.data?.detail;
      setFehler(typeof d === 'string' ? d : 'Anlegen fehlgeschlagen — nochmal.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="fd-verlosung">
      <h2>Probetraining zuweisen</h2>
      <input className="fd-suche" placeholder="Gast suchen: Name oder Telefonnummer"
             value={q} onChange={(e) => setQ(e.target.value)} />
      {fehler && (
        <p className="fd-fehler">{fehler}{' '}
          <button className="fd-btn fd-btn-line" onClick={() => {
            setFehler('');
            if (aktiv) kurseLaden(aktiv); else suchen(q);
          }}>Nochmal</button></p>
      )}

      {treffer?.length === 0 && !neu && (
        <p className="fd-lade">Kein Treffer.{' '}
          <button className="fd-btn" onClick={() => setNeu(true)}>Neuen Gast anlegen</button></p>
      )}
      {neu && (
        <div className="fd-neuform">
          {[['vorname', 'Vorname *'], ['nachname', 'Nachname'], ['telefon', 'Telefonnummer *']]
            .map(([f, label]) => (
            <input key={f} className="fd-suche" placeholder={label} value={neuForm[f]}
                   onChange={(e) => setNeuForm((p) => ({ ...p, [f]: e.target.value }))} />
          ))}
          <button className="fd-btn fd-btn-gross" disabled={busy} onClick={neuAnlegen}>
            Anlegen & Termin wählen</button>
        </div>
      )}

      {(treffer || []).map((k) => (
        <div key={k.karten_code} className="fd-treffer">
          <div className="fd-treffer-info">
            <b>{k.name}</b>
            <span className={`fd-band fd-band-${k.band || 'gast'}`}>{k.band || 'gast'}</span>
            {k.probetrainings.filter((b) => b.status !== 'storniert').map((b) => (
              <span key={b.buchung_id} className="fd-lade"> · ✓ {b.kurs} {b.datum} {b.uhrzeit}</span>
            ))}
          </div>
          <button className="fd-btn" onClick={() => kurseLaden(k)}>Termin zuweisen</button>
        </div>
      ))}

      {aktiv && (
        <div className="fd-zuweisen">
          <p className="fd-lade">Termin für <b>{aktiv.name}</b> — bei Kinderkursen
            Vorname des Kindes:</p>
          <input className="fd-suche" placeholder="Vorname des Kindes (nur bei Kids)"
                 value={kindName} onChange={(e) => setKindName(e.target.value)} />
          {kurse === null && <p className="fd-lade">Kurse laden …</p>}
          {(kurse || []).slice(0, 10).map((kk) => (
            <button key={kk.termin_id} className="fd-kurswahl" disabled={busy}
                    onClick={() => zuweisen(kk.termin_id)}>
              <b>{kk.wochentag} {kk.start.slice(8, 10)}.{kk.start.slice(5, 7)}. {kk.start.slice(11, 16)}</b>
              {' '}{kk.kurs} <span className="fd-lade">({kk.frei} frei)</span>
            </button>
          ))}
          <button className="fd-btn fd-btn-line" onClick={() => setAktiv(null)}>Abbrechen</button>
        </div>
      )}
    </section>
  );
}


const CSS = `
.fd-suche { width: 100%; background: #17171B; color: inherit; font: inherit;
  font-size: 17px; border: 1px solid rgba(245,245,244,.25); border-radius: 12px;
  padding: 15px; margin: 8px 0; }
.fd-treffer { display: flex; align-items: center; justify-content: space-between;
  gap: 12px; background: #17171B; border: 1px solid rgba(245,245,244,.14);
  border-radius: 12px; padding: 13px 15px; margin: 8px 0; flex-wrap: wrap; }
.fd-treffer-info { min-width: 0; flex: 1 1 auto; }
/* Buchungs-Haken als eigene Zeile — sonst liegt der lange Text am Handy
   ueber dem Zuweisen-Knopf und der Tap geht ins Leere. */
.fd-treffer-info .fd-lade { display: block; margin: 2px 0 0; }
.fd-treffer > .fd-btn { flex: 0 0 auto; min-height: 48px; }
.fd-band { display: inline-block; margin-left: 8px; padding: 2px 9px;
  border-radius: 100px; font-size: 11px; text-transform: uppercase;
  border: 1px solid rgba(245,245,244,.3); }
.fd-band-mitglied { border-color: #3FBF6A; color: #3FBF6A; }
.fd-kurswahl { display: block; width: 100%; text-align: left; background: #17171B;
  color: inherit; font: inherit; font-size: 16px; border: 1px solid
  rgba(245,245,244,.18); border-radius: 12px; padding: 15px; margin: 8px 0;
  cursor: pointer; min-height: 54px; }
.fd-kurswahl:disabled { opacity: .5; }
.fd-fehler { color: #ff8a80; margin: 8px 0; }
.fd-neuform { margin: 8px 0; }
.fd-zuweisen { margin-top: 10px; border-top: 1px solid rgba(245,245,244,.14);
  padding-top: 10px; }

.fd-root { min-height: 100vh; background: #0E0E10; color: #F5F5F4;
  font-family: Inter, system-ui, sans-serif; padding: 22px 20px 60px; }
.fd-root * { box-sizing: border-box; }
.fd-root h1 { font-family: Anton, sans-serif; font-weight: 400; font-size: 30px;
  letter-spacing: .05em; text-transform: uppercase; margin: 0; }
.fd-root h2 { font-size: 15px; letter-spacing: .14em; text-transform: uppercase;
  color: #f5821f; margin: 34px 0 14px; }
.fd-root h3 { font-size: 16px; margin: 0 0 8px; }
.fd-lade { opacity: .7; padding: 40px; text-align: center; }
.fd-kopf { display: flex; flex-wrap: wrap; gap: 14px; justify-content: space-between;
  align-items: center; max-width: 1100px; margin: 0 auto; }
.fd-knoepfe { display: flex; flex-wrap: wrap; gap: 10px; }
.fd-btn { font: inherit; font-weight: 600; font-size: 14.5px; color: #fff;
  background: #f5821f; border: none; border-radius: 999px; padding: 11px 18px;
  cursor: pointer; text-decoration: none; display: inline-block; }
.fd-btn:disabled { opacity: .55; }
.fd-btn-line { background: transparent; border: 1px solid rgba(255,255,255,.25); font-weight: 500; }
.fd-btn-gross { font-size: 19px; padding: 16px 30px; }
.fd-kacheln { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px; max-width: 1100px; margin: 26px auto 0; }
.fd-kachel { background: #17171A; border: 1px solid rgba(255,255,255,.1);
  border-radius: 14px; padding: 16px; display: flex; flex-direction: column; }
.fd-wert { font-family: Anton, sans-serif; font-size: 34px; line-height: 1.1; }
.fd-label { font-size: 13px; opacity: .65; margin-top: 4px; }
.fd-verlosung, .fd-root > section { max-width: 1100px; margin: 0 auto; }
.fd-gewinner { background: #f5821f; border-radius: 18px; padding: 22px;
  margin: 16px 0; display: flex; flex-direction: column; align-items: center; gap: 4px; }
.fd-gewinner-nr { font-family: Anton, sans-serif; font-size: clamp(50px, 10vw, 90px); line-height: 1; }
.fd-gewinner-name { font-size: 26px; font-weight: 700; }
.fd-gewinner-topf { opacity: .8; font-size: 14px; }
.fd-preisrow { display: flex; gap: 10px; margin-top: 14px; width: 100%; max-width: 420px; }
.fd-preisrow input { flex: 1; font: inherit; color: #0E0E10; border: none;
  border-radius: 10px; padding: 11px 13px; min-width: 0; }
.fd-preisrow .fd-btn { background: #0E0E10; }
.fd-tabelle { width: 100%; max-width: 460px; border-collapse: collapse; margin-top: 8px; }
.fd-tabelle th, .fd-tabelle td { text-align: left; padding: 8px 10px;
  border-bottom: 1px solid rgba(255,255,255,.1); font-size: 14.5px; }
.fd-tabelle th { opacity: .6; font-weight: 500; }
.fd-boards { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 14px; }
.fd-board { background: #17171A; border: 1px solid rgba(255,255,255,.1);
  border-radius: 14px; padding: 16px; }
.fd-einheit { opacity: .5; font-weight: 400; font-size: 13px; }
.fd-kat { font-size: 12px; letter-spacing: .1em; text-transform: uppercase; opacity: .55; }
.fd-board ol { margin: 4px 0 10px; padding-left: 20px; }
.fd-board li { font-size: 14.5px; margin: 3px 0; }
.fd-leer { opacity: .5; font-size: 14px; }
`;
